import { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useThemeClasses } from "@/hooks/useThemeClasses";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Smartphone, Plus, Search, Edit, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";

const ITEMS_PER_PAGE = 15;

const Modelos = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const lojaSlug = slug || user?.loja_slug;
  const { isDark, card, input, textPrimary, textSecondary, textMuted, tableRow, button } = useThemeClasses();
  const [modelos, setModelos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => { 
    if (lojaSlug) {
      setLoading(true);
      fetchModelos(); 
    }
  }, [lojaSlug, location.key]);
  useEffect(() => { setCurrentPage(1); }, [search]);

  const fetchModelos = async () => {
    try {
      const response = await axios.get(`${API}/loja/${lojaSlug}/modelos`);
      setModelos(response.data);
    } catch (error) {
      toast.error("Erro ao carregar modelos");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/loja/${lojaSlug}/modelos/${deleteId}`);
      toast.success("Modelo excluído com sucesso");
      fetchModelos();
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao excluir modelo";
      toast.error(message);
    } finally {
      setDeleteId(null);
    }
  };

  const filteredModelos = modelos.filter((modelo) =>
    modelo.nome?.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredModelos.length / ITEMS_PER_PAGE);
  const paginatedModelos = filteredModelos.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-[#D4AF37]">Carregando...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="modelos-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
            <Smartphone className="w-5 h-5 text-[#D4AF37]" />
          </div>
          <div>
            <h1 className={`text-2xl font-bold font-['Outfit'] ${textPrimary}`}>Modelos</h1>
            <p className={`text-sm ${textMuted}`}>{filteredModelos.length} modelos cadastrados</p>
          </div>
        </div>
        <Link to={`/${lojaSlug}/modelos/novo`}>
          <Button className="bg-[#D4AF37] text-black font-bold hover:bg-[#B5952F]" data-testid="btn-novo-modelo">
            <Plus className="w-4 h-4 mr-2" />Novo Modelo
          </Button>
        </Link>
      </div>

      <Card className={card}>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input 
              placeholder="Buscar modelo..." 
              value={search} 
              onChange={(e) => setSearch(e.target.value)}
              className={`pl-10 focus:border-[#D4AF37] ${input}`}
              data-testid="search-modelo" 
            />
          </div>
        </CardContent>
      </Card>

      <Card className={card}>
        <CardContent className="p-0">
          {paginatedModelos.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow className={`${isDark ? 'border-white/5' : 'border-gray-100'} hover:bg-transparent`}>
                    <TableHead className={`uppercase text-xs tracking-wider font-medium ${textMuted}`}>Nome</TableHead>
                    <TableHead className={`uppercase text-xs tracking-wider font-medium text-center ${textMuted}`}>Quantidade</TableHead>
                    <TableHead className={`uppercase text-xs tracking-wider font-medium text-right ${textMuted}`}>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedModelos.map((modelo) => (
                    <TableRow key={modelo.id} className={tableRow}>
                      <TableCell className={`font-medium ${textSecondary}`}>{modelo.nome}</TableCell>
                      <TableCell className="text-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${modelo.quantidade_produtos > 0 ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
                          {modelo.quantidade_produtos}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/${lojaSlug}/modelos/${modelo.id}`}>
                            <Button size="sm" variant="ghost" className="text-gray-400 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Link to={`/${lojaSlug}/modelos/editar/${modelo.id}`}>
                            <Button size="sm" variant="ghost" className="text-gray-400 hover:text-blue-400 hover:bg-blue-400/10">
                              <Edit className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-400 hover:bg-red-400/10" onClick={() => setDeleteId(modelo.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredModelos.length}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPage}
              />
            </>
          ) : (
            <div className="p-12 text-center">
              <Smartphone className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className={textMuted}>Nenhum modelo encontrado</p>
              <Link to={`/${lojaSlug}/modelos/novo`}>
                <Button className="mt-4 bg-[#D4AF37] text-black font-bold hover:bg-[#B5952F]">
                  <Plus className="w-4 h-4 mr-2" />Adicionar Modelo
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className={isDark ? 'bg-[#141414] border-white/10' : 'bg-white border-gray-200'}>
          <AlertDialogHeader>
            <AlertDialogTitle className={textPrimary}>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription className={textMuted}>Tem certeza que deseja excluir este modelo?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={isDark ? 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10' : 'bg-gray-100 border-gray-200 text-gray-700'}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 text-white hover:bg-red-600">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Modelos;
